// Copyright 2018 Google LLC
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     https://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
/**
 * @module elements-sk/tabs-sk
 * @description <h2><code>tabs-sk</code></h2>
 *
 * <p>
 * The tabs-sk custom element declaration, used in conjunction with button and
 * the [tabs-panel-sk]{@link module:elements-sk/tabs-panel-sk} element
 * allows you to create tabbed interfaces. The association between the buttons
 * and the tabs displayed in [tabs-panel-sk]{@link module:elements-sk/tabs-panel-sk}
 * is document order, i.e. the first button shows the first panel, second
 * button shows second panel, etc.
 * </p>
 *
 * @example
 *
 * <tabs-sk>
 *   <button class=selected>Query</button>
 *   <button>Results</button>
 * </tabs-sk>
 * <tabs-panel-sk>
 *   <div>
 *     This is the query tab.
 *   </div>
 *   <div>
 *     This is the results tab.
 *   </div>
 * </tabs-panel-sk>
 *
 * @attr selected - The index of the selected tab.
 *
 * @evt tab-selected-sk - Event sent when the user clicks on a tab. The events
 *        value of detail.index is the index of the selected tab.
 *
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "TabsSk", {
    enumerable: true,
    get: function() {
        return TabsSk;
    }
});
const _lit = require("lit");
const _decorators = require("lit/decorators.js");
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
let TabsSk = class TabsSk extends _lit.LitElement {
    createRenderRoot() {
        return this;
    }
    render() {
        return _lit.noChange;
    }
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('click', this);
        // Ensure initial state is consistent
        this._updateChildren();
        this._updatePanel();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('click', this);
    }
    handleEvent(e) {
        e.stopPropagation();
        this.querySelectorAll('button').forEach((ele, i)=>{
            if (ele === e.target) {
                this.select(i, true);
            }
        });
    }
    updated(changedProperties) {
        if (changedProperties.has('selected')) {
            this._updateChildren();
            this._updatePanel();
        }
    }
    _updateChildren() {
        this.querySelectorAll('button').forEach((ele, i)=>{
            ele.classList.toggle('selected', i === this.selected);
        });
    }
    _updatePanel() {
        var _this_nextElementSibling;
        if (((_this_nextElementSibling = this.nextElementSibling) === null || _this_nextElementSibling === void 0 ? void 0 : _this_nextElementSibling.tagName) === 'TABS-PANEL-SK') {
            this.nextElementSibling.setAttribute('selected', String(this.selected));
        }
    }
    /**
   * Force the selection of a tab
   *
   * @param index The index of the tab to select.
   * @param trigger If true then trigger the 'tab-selected-sk' event.
   */ select(index, trigger = false) {
        this.selected = index;
        if (trigger) {
            this.dispatchEvent(new CustomEvent('tab-selected-sk', {
                bubbles: true,
                detail: {
                    index: index
                }
            }));
        }
    }
    constructor(...args){
        super(...args);
        this.selected = 0;
    }
};
_ts_decorate([
    (0, _decorators.property)({
        type: Number,
        reflect: true
    }),
    _ts_metadata("design:type", Number)
], TabsSk.prototype, "selected", void 0);
TabsSk = _ts_decorate([
    (0, _decorators.customElement)('tabs-sk')
], TabsSk);
